import { useState, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Gear,
  Phone,
  Envelope,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
  Plus,
  Trash,
  Copy,
  Buildings,
  Key
} from '@phosphor-icons/react';

const ALL_MCP_SCOPES = [
  { id: 'leads:read', label: 'Read leads' },
  { id: 'leads:write', label: 'Write leads' },
  { id: 'scripts:read', label: 'Read scripts' },
  { id: 'scripts:write', label: 'Write scripts' },
  { id: 'calls:read', label: 'Read calls' },
  { id: 'calls:initiate', label: 'Place calls (dangerous)' },
  { id: 'queue:read', label: 'Read queue' },
  { id: 'queue:control', label: 'Control queue (dangerous)' },
  { id: 'dashboard:read', label: 'Read dashboard stats' }
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney'
];

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState({
    companyName: '',
    companyDescription: '',
    agentName: 'Sarah',
    timezone: 'America/New_York',
    transferPhoneNumber: ''
  });
  const [webhooks, setWebhooks] = useState([]);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ name: '', source: 'custom', scriptId: '' });
  const [scripts, setScripts] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState({ name: '', scopes: [] });
  const [createdKey, setCreatedKey] = useState(null);

  useEffect(() => {
    loadSettings();
    loadWebhooks();
    loadScripts();
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const response = await api.get('/api-keys');
      setApiKeys(response.data.keys);
    } catch (error) {
      // Silently fail
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKey.name || newKey.scopes.length === 0) {
      toast.error('Give the key a name and at least one scope');
      return;
    }
    try {
      const response = await api.post('/api-keys', newKey);
      setCreatedKey(response.data);
      setNewKey({ name: '', scopes: [] });
      loadApiKeys();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleRevokeApiKey = async (id) => {
    if (!window.confirm('Revoke this API key? Any app using it will stop working.')) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success('API key revoked');
      loadApiKeys();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setProfile(response.data.profile);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadWebhooks = async () => {
    try {
      const response = await api.get('/webhooks');
      setWebhooks(response.data.webhooks);
    } catch (error) {
      // Silently fail
    }
  };

  const loadScripts = async () => {
    try {
      const response = await api.get('/scripts');
      setScripts(response.data.scripts);
    } catch (error) {
      // Silently fail
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/settings/profile', profile);
      toast.success('Profile updated');
      checkAuth(); // Refresh user data
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      await api.post('/webhooks', newWebhook);
      toast.success('Webhook created');
      setShowWebhookModal(false);
      setNewWebhook({ name: '', source: 'custom', scriptId: '' });
      loadWebhooks();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success('Webhook deleted');
      loadWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const copyWebhookUrl = (token) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/ingest/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied to clipboard');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-medium text-zinc-50 tracking-tight">Settings</h1>
        <p className="text-zinc-400 mt-1">Manage your account and integrations</p>
      </div>

      {/* Company Profile */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
            <Buildings size={20} className="text-zinc-400" />
          </div>
          <h3 className="text-lg font-medium text-zinc-50">Company Profile</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-400">Company Name</Label>
            <Input
              value={profile.companyName || ''}
              onChange={(e) => setProfile({ ...profile, companyName: e.target.value })}
              placeholder="Acme Inc."
              className="bg-zinc-800 border-zinc-700"
              data-testid="company-name-input"
            />
          </div>
          <div>
            <Label className="text-zinc-400">AI Agent Name</Label>
            <Input
              value={profile.agentName || ''}
              onChange={(e) => setProfile({ ...profile, agentName: e.target.value })}
              placeholder="Sarah"
              className="bg-zinc-800 border-zinc-700"
              data-testid="agent-name-input"
            />
          </div>
          <div>
            <Label className="text-zinc-400">Timezone</Label>
            <Select value={profile.timezone} onValueChange={(v) => setProfile({ ...profile, timezone: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="timezone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(tz => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400">Human Handoff Number</Label>
            <Input
              value={profile.transferPhoneNumber || ''}
              onChange={(e) => setProfile({ ...profile, transferPhoneNumber: e.target.value })}
              placeholder="+15551234567"
              className="bg-zinc-800 border-zinc-700"
              data-testid="transfer-phone-input"
            />
            <p className="text-xs text-zinc-500 mt-1">
              When a caller asks for a human, the AI bridges the call to this number. Leave blank to disable.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-400">Company Description</Label>
            <Textarea
              value={profile.companyDescription || ''}
              onChange={(e) => setProfile({ ...profile, companyDescription: e.target.value })}
              placeholder="Brief description of your company for the AI to reference"
              className="bg-zinc-800 border-zinc-700"
              rows={3}
              data-testid="company-description-input"
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-profile-btn"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>

      {/* Integration Status */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
            <Gear size={20} className="text-zinc-400" />
          </div>
          <h3 className="text-lg font-medium text-zinc-50">Integration Status</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <IntegrationCard
            name="Twilio (Calling)"
            connected={settings?.integrations?.twilio}
            detail={settings?.integrations?.twilioPhone}
          />
          <IntegrationCard
            name="Claude AI"
            connected={settings?.integrations?.claude}
            detail="Powered by Emergent"
          />
          <IntegrationCard
            name="ElevenLabs (Voice)"
            connected={settings?.integrations?.elevenlabs}
          />
          <IntegrationCard
            name="Resend (Email)"
            connected={settings?.integrations?.resend}
          />
          <IntegrationCard
            name="Calendly"
            connected={settings?.integrations?.calendly}
          />
          <IntegrationCard
            name="Human Handoff"
            connected={settings?.integrations?.handoff}
            detail={settings?.profile?.transferPhoneNumber || undefined}
          />
        </div>

        <div className="mt-4 p-4 bg-zinc-900 rounded-sm border border-zinc-800">
          <p className="text-sm text-zinc-400">
            To configure integrations, add the API keys to your environment variables and restart the server.
            Contact support if you need help setting up integrations.
          </p>
        </div>
      </div>

      {/* Webhooks */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
              <LinkIcon size={20} className="text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-50">Webhooks</h3>
          </div>
          <Button
            onClick={() => setShowWebhookModal(true)}
            variant="outline"
            className="border-zinc-700 flex items-center gap-2"
            data-testid="create-webhook-btn"
          >
            <Plus size={16} /> Create Webhook
          </Button>
        </div>

        {webhooks.length > 0 ? (
          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <div
                key={webhook.id}
                className="bg-zinc-900 rounded-sm p-4 border border-zinc-800 flex items-center justify-between"
                data-testid={`webhook-${webhook.id}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-50">{webhook.name}</span>
                    <span className={`badge ${webhook.isActive ? 'badge-success' : 'badge-neutral'}`}>
                      {webhook.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-500 mt-1">
                    Source: {webhook.source} | Token: {webhook.token.slice(0, 12)}...
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyWebhookUrl(webhook.token)}
                    className="text-zinc-400 hover:text-zinc-50"
                  >
                    <Copy size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteWebhook(webhook.id)}
                    className="text-zinc-400 hover:text-red-400"
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500">
            <LinkIcon size={32} className="mx-auto mb-2 opacity-50" />
            <p>No webhooks configured</p>
            <p className="text-sm mt-1">Create a webhook to receive leads from external sources</p>
          </div>
        )}
      </div>

      {/* API Keys (for MCP consumers) */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
              <Key size={20} className="text-zinc-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-zinc-50">API Keys</h3>
              <p className="text-xs text-zinc-500">Used by external apps to call ALLO's MCP endpoint at <code className="bg-zinc-800 px-1 rounded">/mcp</code></p>
            </div>
          </div>
          <Button
            onClick={() => { setCreatedKey(null); setShowKeyModal(true); }}
            variant="outline"
            className="border-zinc-700 flex items-center gap-2"
            data-testid="create-api-key-btn"
          >
            <Plus size={16} /> New API Key
          </Button>
        </div>

        {apiKeys.length > 0 ? (
          <div className="space-y-3">
            {apiKeys.map((k) => (
              <div
                key={k.id}
                className="bg-zinc-900 rounded-sm p-4 border border-zinc-800 flex items-center justify-between"
                data-testid={`api-key-${k.id}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-50">{k.name}</span>
                    <span className={`badge ${k.revokedAt ? 'badge-neutral' : 'badge-success'}`}>
                      {k.revokedAt ? 'Revoked' : 'Active'}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-500 mt-1 font-mono">
                    {k.keyPrefix}… · scopes: {(k.scopes || []).join(', ') || 'none'}
                  </div>
                  {k.lastUsedAt && (
                    <div className="text-xs text-zinc-600 mt-1">
                      Last used {new Date(k.lastUsedAt).toLocaleString()}
                    </div>
                  )}
                </div>
                {!k.revokedAt && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeApiKey(k.id)}
                    className="text-zinc-400 hover:text-red-400"
                  >
                    <Trash size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500">
            <Key size={32} className="mx-auto mb-2 opacity-50" />
            <p>No API keys yet</p>
            <p className="text-sm mt-1">Create one to let another app drive ALLO via its MCP endpoint</p>
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      <Dialog open={showKeyModal} onOpenChange={(v) => { setShowKeyModal(v); if (!v) setCreatedKey(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="create-api-key-modal">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              {createdKey ? 'API Key Created' : 'Create API Key'}
            </DialogTitle>
          </DialogHeader>
          {createdKey ? (
            <div className="space-y-4">
              <div className="p-3 bg-yellow-950/40 border border-yellow-600/30 rounded-sm text-sm text-yellow-200">
                Copy this key now — it will not be shown again. If you lose it, revoke it and create a new one.
              </div>
              <div>
                <Label className="text-zinc-400">Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    readOnly
                    value={createdKey.key}
                    className="bg-zinc-800 border-zinc-700 font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(createdKey.key, 'API key')}
                    className="border-zinc-700"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                Use with <code className="bg-zinc-800 px-1 rounded">X-API-Key</code> header when calling <code className="bg-zinc-800 px-1 rounded">/mcp</code>.
              </div>
              <DialogFooter>
                <Button
                  onClick={() => { setShowKeyModal(false); setCreatedKey(null); }}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400">Name</Label>
                  <Input
                    value={newKey.name}
                    onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                    placeholder="e.g., Marketing site MCP client"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="api-key-name-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Scopes</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {ALL_MCP_SCOPES.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newKey.scopes.includes(s.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...newKey.scopes, s.id]
                              : newKey.scopes.filter((x) => x !== s.id);
                            setNewKey({ ...newKey, scopes: next });
                          }}
                          className="accent-blue-500"
                        />
                        <span>{s.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Grant only what the consuming app needs. Dangerous tools (place calls / control queue) also require <code className="bg-zinc-800 px-1 rounded">confirm: true</code> per call.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowKeyModal(false)} className="border-zinc-700">
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={!newKey.name || newKey.scopes.length === 0}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="confirm-create-api-key-btn"
                >
                  Create Key
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Webhook Modal */}
      <Dialog open={showWebhookModal} onOpenChange={setShowWebhookModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="create-webhook-modal">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Create Webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Name</Label>
              <Input
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                placeholder="e.g., Typeform Leads"
                className="bg-zinc-800 border-zinc-700"
                data-testid="webhook-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Source</Label>
              <Select value={newWebhook.source} onValueChange={(v) => setNewWebhook({ ...newWebhook, source: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="meta">Meta Lead Ads</SelectItem>
                  <SelectItem value="typeform">Typeform</SelectItem>
                  <SelectItem value="hubspot">HubSpot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400">Auto-assign Script (optional)</Label>
              <Select value={newWebhook.scriptId || "none"} onValueChange={(v) => setNewWebhook({ ...newWebhook, scriptId: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Select a script" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {scripts.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                If set, leads will be automatically queued with this script
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhookModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleCreateWebhook}
              disabled={!newWebhook.name}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="confirm-create-webhook-btn"
            >
              Create Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IntegrationCard({ name, connected, detail }) {
  return (
    <div className="bg-zinc-900 rounded-sm p-4 border border-zinc-800" data-testid={`integration-${name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-zinc-50">{name}</span>
        {connected ? (
          <CheckCircle size={20} className="text-emerald-400" weight="fill" />
        ) : (
          <XCircle size={20} className="text-zinc-500" weight="fill" />
        )}
      </div>
      <div className="text-sm text-zinc-500">
        {connected ? (detail || 'Connected') : 'Not configured'}
      </div>
    </div>
  );
}
